import VideoPlayer from 'react-player'
import { useRef, createContext, useContext, useState, useEffect } from 'react'
import { Play } from 'lucide-react'

const PlayerContext = createContext({
	sermon: null,
	setSermon: () => {},
	close: () => {}
});

/**
 * Utility hook to access the player context
 */
export const usePlayer = () => useContext(PlayerContext)

/**
 * The player context provider
 *
 * @param {Object} props - The component's props
 * @param {React.ReactNode} props.children - The children to render
 * @returns {JSX.Element}
 */
export const PlayerProvider = ({ children }) => {
	const [sermon, setSermon] = useState(null)
	const dialogRef = useRef(null)

	const close = () => setSermon(null)

	useEffect(() => {
		if(sermon) {
			dialogRef.current.showModal()
		} else {
			dialogRef.current.close()
		}
	}, [sermon])

	return (
		<PlayerContext.Provider value={{ sermon, setSermon, close }}>
			<dialog ref={dialogRef} className="series--sermon-dialog">
				<button className='gil-button is-small series--sermon-dialog--close' onClick={close}>
					Close
				</button>
				{sermon && <Player sermon={sermon} />}
			</dialog>
			{children}
		</PlayerContext.Provider>
	)
}

/**
 * A player component for sermons
 *
 * @param {Object} props - The component's props
 * @param {Object} props.sermon - The sermon object
 * @returns {JSX.Element}
 */
function Player({ sermon }) {
	const [url, setUrl] = useState(sermon.video || sermon.audio)


	return (
		<div className="series--sermon--player">
			<div className='series--sermon--player-metadata'>
				<h3>{sermon.title}</h3>
				{
					!!sermon.speaker.length &&
					<p><strong>Speaker:</strong> {sermon.speaker.join(', ')}</p>
				}
				{
					!!sermon.scripture.length &&
					<p><strong>Scripture:</strong> {sermon.scripture.join(', ')}</p>
				}
				{
					!!sermon.date &&
					<p><strong>Year:</strong> {sermon.date}</p>
				}
				{
					!!sermon.duration &&
					<p><strong>Duration:</strong> {sermon.duration}</p>
				}
			</div>
			<div className='series--sermon--player-controls'>
				{
					url && url === sermon.video &&
					<VideoPlayer
						url={url}
						controls
						autoPlay
						width='100%'
						height='auto'
					/>
				}

				{
					url && url === sermon.video && !!sermon.audio &&
					<button className="gil-button is-large series--sermon--player-audio" onClick={() => setUrl(sermon.audio)}>
						<Play />
						Play Audio
					</button>
				}

				{
					url && url === sermon.audio &&
					<div className='series--sermon--player-audio-backdrop'>
						<img src={sermon.imageUrl} alt="" />
						<audio controls autoPlay>
							<source src={url} />
							Your browser does not support the audio element.
						</audio>
					</div>
				}

				{
					url && url === sermon.audio && !!sermon.video &&
					<button className="gil-button is-large series--sermon--player-video" onClick={() => setUrl(sermon.video)}>
						<Play />
						Play Video
					</button>
				}
				
			</div>
		</div>
	)
}
